import React, { useState, useContext, useMemo, useEffect, useRef } from "react";
import { Collapse, List, ListItem, Typography, makeStyles } from "@material-ui/core";
import ExpandIcon from "@material-ui/icons/ExpandMore";
import { useLocation, NavLink } from "react-router-dom";
import MenuContext from "./contextTypes";
import { arrAdd, arrDel, flatternArray } from "./util";
var useStyles = makeStyles(function (theme) {
  return {
    root: {
      height: 56,
      backgroundColor: "transparent",
      color: "#bdbdbd",
      borderLeftWidth: 4,
      borderLeftColor: "transparent",
      borderLeftStyle: "solid"
    },
    selected: {
      backgroundColor: "rgba(180, 205, 194, 0.08) !important",
      color: "white",
      borderLeftColor: theme.palette.primary.main
    },
    label: {
      flex: 1
    },
    expand: {
      float: "right"
    },
    expanded: {
      transform: "rotateX(180deg)"
    },
    suffix: {
      paddingLeft: "9px",
      paddingTop: "6.5px",
      fontSize: "10px",
      fontWeight: "bold",
      color: "#7384E2"
    }
  };
});

var MenuItem = function MenuItem(_a) {
  var node = _a.node;
  var name = node.name,
      path = node.path,
      _b = node.children,
      children = _b === void 0 ? [] : _b,
      depth = node.depth,
      icon = node.icon,
      _c = node.exact,
      exact = _c === void 0 ? false : _c,
      key = node.key,
      suffix = node.suffix;
  var context = useContext(MenuContext);
  var location = useLocation();
  var expandedKeys = context.expandedKeys,
      onNodeExpand = context.onNodeExpand,
      hoveredMenuKey = context.hoveredMenuKey,
      setHoveredMenuKey = context.setHoveredMenuKey,
      onLeafClick = context.onLeafClick;
  var classes = useStyles();
  var active = useMemo(function () {
    if (location.pathname) {
      return location.pathname.indexOf(path) === 0 && (children || []).length === 0;
    }

    return false;
  }, [location.pathname, path, children]);
  var hovered = useMemo(function () {
    return hoveredMenuKey === key || location.pathname.indexOf(path) === 0;
  }, [hoveredMenuKey, key, path, location.pathname]);
  var expanded = useMemo(function () {
    return expandedKeys.includes(key);
  }, [expandedKeys, key]);

  function onExpand(event) {
    onNodeExpand(event, {
      path: path,
      expanded: expanded,
      key: key
    });
  }

  function onClick(event) {
    onLeafClick(event, {
      path: path,
      expanded: expanded,
      key: key
    });
  }

  function renderIcon(icon) {
    return icon && React.cloneElement(icon, {
      style: {
        color: active || hovered || expanded ? "white" : "#b7b7bd",
        marginRight: 12
      }
    });
  }

  function renderLabel() {
    return React.createElement(ListItem, {
      style: {
        paddingLeft: paddingLeft,
        height: depth === 0 ? 56 : 48,
        color: hovered || expanded ? "white" : "#b7b7bd"
      },
      selected: active,
      classes: {
        root: classes.root,
        selected: classes.selected
      },
      onMouseOver: function onMouseOver(e) {
        setHoveredMenuKey(key);
      },
      button: true,
      onClick: children.length > 0 ? onExpand : onClick
    }, renderIcon(icon), React.createElement("div", {
      style: {
        display: "flex"
      }
    }, React.createElement(Typography, {
      className: classes.label,
      spellCheck: false
    }, name), React.createElement(Typography, {
      className: classes.suffix
    }, suffix)), children.length > 0 && React.createElement(ExpandIcon, {
      className: "".concat(classes.expand, " ").concat(expanded ? classes.expanded : "")
    }));
  }

  var paddingLeft = useMemo(function () {
    return 24 + depth * 32;
  }, [depth]);
  return React.createElement("div", null, children.length > 0 ? renderLabel() : React.createElement(NavLink, {
    to: path || "",
    exact: exact,
    style: {
      textDecoration: "none"
    }
  }, renderLabel()), children.length > 0 && expanded && React.createElement(Collapse, {
    in: true
  }, children.map(function (item, index) {
    return React.createElement(MenuItem, {
      key: index,
      node: item
    });
  })));
};

var SideMenu = function SideMenu(_a) {
  var _b = _a.data,
      data = _b === void 0 ? [] : _b,
      classes = _a.classes,
      className = _a.className,
      style = _a.style;
  var location = useLocation();
  var dirty = useRef(false);

  var _c = useState([]),
      expandedKeys = _c[0],
      setExpandedKeys = _c[1];

  var _d = useState(null),
      hoveredMenuKey = _d[0],
      setHoveredMenuKey = _d[1];

  var flatternData = useMemo(function () {
    return flatternArray(data);
  }, [data]);
  useEffect(function () {
    if (!dirty.current && flatternData && flatternData.length > 0) {
      dirty.current = true;
      flatternData.forEach(function (item) {
        if ((item.children || []).some(function (v) {
          return v.path === location.pathname;
        })) {
          setExpandedKeys([item.key]);
        }
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [flatternData]);

  function onNodeExpand(e, menuNode) {
    var key = menuNode.key,
        expanded = menuNode.expanded;
    var targetExpanded = !expanded;
    var newExpandedKeys;

    if (targetExpanded) {
      newExpandedKeys = arrAdd(expandedKeys, key);
    } else {
      newExpandedKeys = arrDel(expandedKeys, key);
    }

    setExpandedKeys(newExpandedKeys);
  }

  function onLeafClick(e, menuNode) {// const { path } = menuNode;
  }

  return React.createElement(MenuContext.Provider, {
    value: {
      expandedKeys: expandedKeys,
      onNodeExpand: onNodeExpand,
      hoveredMenuKey: hoveredMenuKey,
      setHoveredMenuKey: setHoveredMenuKey,
      onLeafClick: onLeafClick
    }
  }, React.createElement(List, {
    component: "ul",
    classes: classes,
    className: className,
    style: style,
    onMouseOut: function onMouseOut() {
      setHoveredMenuKey(null);
    }
  }, flatternData.map(function (item, index) {
    return React.createElement(MenuItem, {
      key: index,
      node: item
    });
  })));
};

export default SideMenu;